import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';
import { ControlledTooltip } from '@shared/components/ControlledTooltip';
import { ConfirmDialog, type TConfirmDialogState } from '@shared/components/dialogs/ConfirmDialog';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import {
  useCreateChangeTemplateAssessment,
  useDeleteChangeTemplateAssessment,
  useReorderChangeTemplateAssessments,
  useUpdateChangeTemplateAssessment,
} from '../api';
import { TITLE_PLACEHOLDER } from '@modules/documents/tiptap/nodes/title/constants';
import { CHANGE_TEMPLATE_ASSESSMENTS_MAX } from '../constants';
import { getChangeTemplatePath } from '../paths';
import type { TChangeTemplateAssessment, TChangeTemplateOutletContext } from '../types';
import ChangeTemplateAssessmentDialog from '../components/ChangeTemplateAssessmentDialog';
import ChangeTemplateAssessmentsTable from '../components/ChangeTemplateAssessmentsTable';

const ChangeTemplateAssessments = () => {
  const {
    details: { changeTemplate, resolvedItems },
    selected,
    canEdit,
    editLockedReason,
    canChange,
    assessmentsDisabledReason: disabledReason,
    templateVersion,
  } = useOutletContext<TChangeTemplateOutletContext>();
  const changeTemplateId = changeTemplate._id;
  const { assessments } = selected;
  const navigate = useNavigate();
  const create = useCreateChangeTemplateAssessment(changeTemplateId);
  const update = useUpdateChangeTemplateAssessment(changeTemplateId);
  const reorder = useReorderChangeTemplateAssessments(changeTemplateId);
  const deleteAssessment = useDeleteChangeTemplateAssessment(changeTemplateId);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState>();
  const reorderDisabledReason = reorder.isPending ? 'Wait for the assessments to finish updating.' : '';
  const addDisabledReason =
    disabledReason ||
    (assessments.length >= CHANGE_TEMPLATE_ASSESSMENTS_MAX
      ? `A ${TERMINOLOGY.changeTemplate.lower} can have at most ${CHANGE_TEMPLATE_ASSESSMENTS_MAX} assessments.`
      : '');

  const onReorder = (sourceId: string, targetId: string) => {
    const assessmentIds = assessments.map((assessment) => assessment._id);
    assessmentIds.splice(assessmentIds.indexOf(sourceId), 1);
    assessmentIds.splice(
      assessments.findIndex((assessment) => assessment._id === targetId),
      0,
      sourceId
    );
    reorder.mutate(assessmentIds);
  };

  const onDelete = (assessment: TChangeTemplateAssessment) => {
    const dialogState: TConfirmDialogState = {
      isOpen: true,
      title: `Remove ${assessment.title || TITLE_PLACEHOLDER}?`,
      content: 'This assessment will be removed from this draft. Completed versions keep it.',
      buttonLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        try {
          await deleteAssessment.mutateAsync(assessment._id);
          setConfirmDialogState({ ...dialogState, isOpen: false });
        } catch {
          /* The API hook reports the failure. Keep the confirmation open for retry. */
        }
      },
    };
    setConfirmDialogState(dialogState);
  };

  return (
    <div className="flex flex-col gap-2">
      {canEdit && (
        <div className="flex">
          <ControlledTooltip
            relationship="inaccessible"
            enable={!!addDisabledReason}
            content={addDisabledReason}
            withArrow
          >
            <Button
              appearance="primary"
              icon={<AddRegular />}
              disabled={!!addDisabledReason}
              onClick={() => setIsCreateOpen(true)}
            >
              Add assessment
            </Button>
          </ControlledTooltip>
        </div>
      )}
      {assessments.length > 0 ? (
        <ChangeTemplateAssessmentsTable
          changeTemplateId={changeTemplateId}
          assessments={assessments}
          resolvedItems={resolvedItems}
          canChange={canChange}
          requiredPending={update.isPending}
          requiredDisabledReason={editLockedReason || disabledReason}
          reorderDisabledReason={reorderDisabledReason}
          onReorder={onReorder}
          onRequiredChange={(changed, required) => update.mutate({ assessmentId: changed._id, required })}
          onDelete={onDelete}
          templateVersion={templateVersion}
        />
      ) : (
        <p className="text-neutral-500">No assessments yet.</p>
      )}
      <ChangeTemplateAssessmentDialog
        isOpen={isCreateOpen}
        setIsOpen={setIsCreateOpen}
        onCreate={async (values) => {
          const assessment = await create.mutateAsync(values);
          // Like a new Document from the toolbar: open it for editing.
          navigate(getChangeTemplatePath(changeTemplateId, { assessmentId: assessment._id, autoEdit: true }));
        }}
      />
      <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
    </div>
  );
};

export default ChangeTemplateAssessments;
